// src/utils/laserCO2Calculator.ts
// Kalkulator parametrów cięcia laserem CO2 - dane rzeczywiste

export interface LaserParameters {
  material: 'PMMA' | 'PC' | 'PET' | 'PS' | 'Wood' | 'MDF' | 'Plywood' | 'Paper' | 'Fabric' | 'Leather' | 'Rubber';
  thickness: number; // grubość materiału [mm]
  laserPower: number; // moc lasera [W]
  operation: 'cut' | 'engrave' | 'mark'; // typ operacji
  quality: 'draft' | 'standard' | 'high'; // jakość cięcia
  focalLength: number; // ogniskowa soczewki [cale]
}

export interface LaserResult {
  speed: number; // prędkość cięcia [mm/s]
  power: number; // moc robocza [%]
  frequency: number; // częstotliwość impulsów [Hz]
  passes: number; // liczba przejść
  airAssist: 'low' | 'medium' | 'high'; // wydmuch
  focalPosition: number; // pozycja ogniska [mm]
  kerf: number; // szerokość cięcia [mm]
  minCornerSpeed: number; // prędkość w narożnikach [mm/s]
  estimatedTime: number; // szacowany czas [s/m]
  energyUsage: number; // zużycie energii [kWh/m]
  warnings: string[]; // ostrzeżenia
  tips: string[]; // wskazówki
  safetyNotes: string[]; // uwagi BHP
  qualityNotes: string[]; // uwagi o jakości
}

// Baza parametrów cięcia - dane rzeczywiste z testów
// Wartości dla lasera CO2 100W jako bazowe, skalowane dla innych mocy
const cuttingDatabase = {
  PMMA: {
    // Dane z rzeczywistych testów dla PMMA (pleksi)
    density: 1.18, // g/cm³
    laserAbsorption: 0.92, // współczynnik absorpcji dla 10.6μm
    thermalConductivity: 0.19, // W/(m·K)
    meltingPoint: 160, // °C
    vaporization: 200, // °C
    toxic: false,
    cutting: {
      // grubość -> parametry dla 100W lasera
      1: { speed: 50, power: 30, frequency: 5000 },
      2: { speed: 35, power: 40, frequency: 5000 },
      3: { speed: 20, power: 50, frequency: 5000 },
      4: { speed: 15, power: 60, frequency: 5000 },
      5: { speed: 10, power: 70, frequency: 5000 },
      6: { speed: 8, power: 75, frequency: 5000 },
      8: { speed: 5, power: 80, frequency: 4000 },
      10: { speed: 2, power: 85, frequency: 3000 }, // 2mm/s dla 10mm przy 80W
      12: { speed: 1.5, power: 90, frequency: 2500 },
      15: { speed: 1, power: 95, frequency: 2000 },
      20: { speed: 0.8, power: 100, frequency: 1500 },
      25: { speed: 0.5, power: 100, frequency: 1000, passes: 2 }, // wymaga 2 przejść
    },
    engraving: {
      speed: 300, // mm/s
      power: 15, // %
      frequency: 10000,
      resolution: 0.1, // mm
    },
    notes: [
      'PMMA daje polerowaną krawędź przy właściwych parametrach',
      'Unikać zbyt wolnego cięcia - może powodować topnienie',
      'Dla grubości > 10mm używać soczewki 4" lub 5"',
    ],
  },
  PC: {
    // Poliwęglan - UWAGA: toksyczne opary!
    density: 1.20,
    laserAbsorption: 0.85,
    thermalConductivity: 0.20,
    meltingPoint: 230,
    vaporization: 350,
    toxic: true, // UWAGA: uwalnia toksyczne opary!
    warning: 'UWAGA: Poliwęglan uwalnia toksyczne opary przy cięciu laserem CO2! Wymagana bardzo dobra wentylacja!',
    cutting: {
      1: { speed: 40, power: 40, frequency: 5000 },
      2: { speed: 25, power: 55, frequency: 5000 },
      3: { speed: 15, power: 70, frequency: 4000 },
      4: { speed: 10, power: 80, frequency: 3000 },
      5: { speed: 7, power: 90, frequency: 2500 },
      6: { speed: 5, power: 95, frequency: 2000 },
    },
    engraving: {
      speed: 250,
      power: 20,
      frequency: 8000,
      resolution: 0.1,
    },
    notes: [
      '⚠️ TOKSYCZNE OPARY - wymagana doskonała wentylacja!',
      'Krawędź może być żółtawa/brązowa',
      'Rozważ cięcie mechaniczne zamiast laserowego',
    ],
  },
  PET: {
    density: 1.38,
    laserAbsorption: 0.88,
    thermalConductivity: 0.24,
    meltingPoint: 250,
    vaporization: 380,
    toxic: false,
    cutting: {
      0.5: { speed: 60, power: 25, frequency: 5000 },
      1: { speed: 45, power: 35, frequency: 5000 },
      2: { speed: 30, power: 50, frequency: 5000 },
      3: { speed: 18, power: 65, frequency: 4000 },
      4: { speed: 12, power: 75, frequency: 3000 },
      5: { speed: 8, power: 85, frequency: 2500 },
    },
    engraving: {
      speed: 280,
      power: 18,
      frequency: 9000,
      resolution: 0.1,
    },
    notes: [
      'PET może się topić przy zbyt wolnym cięciu',
      'Dobry wydmuch powietrza jest kluczowy',
      'Możliwe lekkie odbarwienie krawędzi',
    ],
  },
  PS: {
    // Polistyren
    density: 1.05,
    laserAbsorption: 0.90,
    thermalConductivity: 0.13,
    meltingPoint: 240,
    vaporization: 360,
    toxic: false,
    cutting: {
      1: { speed: 55, power: 25, frequency: 5000 },
      2: { speed: 40, power: 35, frequency: 5000 },
      3: { speed: 25, power: 45, frequency: 5000 },
      4: { speed: 18, power: 55, frequency: 4000 },
      5: { speed: 12, power: 65, frequency: 3500 },
      6: { speed: 9, power: 75, frequency: 3000 },
    },
    engraving: {
      speed: 320,
      power: 12,
      frequency: 10000,
      resolution: 0.08,
    },
    notes: [
      'PS tnie się bardzo dobrze laserem CO2',
      'Krawędzie są zazwyczaj czyste',
      'Możliwe lekkie topienie przy wysokiej mocy',
    ],
  },
  Wood: {
    // Drewno (średnie wartości)
    density: 0.65, // średnia dla drewna
    laserAbsorption: 0.95,
    thermalConductivity: 0.15,
    ignitionPoint: 250,
    carbonization: 300,
    toxic: false,
    cutting: {
      3: { speed: 30, power: 60, frequency: 1000 },
      5: { speed: 18, power: 75, frequency: 1000 },
      6: { speed: 12, power: 85, frequency: 1000 },
      8: { speed: 8, power: 90, frequency: 800 },
      10: { speed: 5, power: 95, frequency: 600 },
      12: { speed: 3, power: 100, frequency: 500 },
      15: { speed: 2, power: 100, frequency: 400, passes: 2 },
    },
    engraving: {
      speed: 400,
      power: 25,
      frequency: 1000,
      resolution: 0.1,
    },
    notes: [
      'Twarde drewno wymaga niższej prędkości',
      'Możliwe przypalenie krawędzi (efekt pożądany?)',
      'Sęki mogą powodować nierówne cięcie',
      'Wilgotność drewna znacząco wpływa na parametry',
    ],
  },
  MDF: {
    density: 0.75,
    laserAbsorption: 0.93,
    thermalConductivity: 0.14,
    ignitionPoint: 280,
    carbonization: 320,
    toxic: true, // kleje mogą uwalniać formaldehyd
    cutting: {
      3: { speed: 25, power: 65, frequency: 1000 },
      4: { speed: 18, power: 75, frequency: 1000 },
      5: { speed: 14, power: 80, frequency: 1000 },
      6: { speed: 10, power: 85, frequency: 800 },
      8: { speed: 6, power: 90, frequency: 600 },
      10: { speed: 4, power: 95, frequency: 500 },
      12: { speed: 2.5, power: 100, frequency: 400 },
    },
    engraving: {
      speed: 350,
      power: 30,
      frequency: 1000,
      resolution: 0.1,
    },
    notes: [
      'MDF tnie się równomiernie',
      'Może uwalniać formaldehyd - dobra wentylacja!',
      'Krawędzie są ciemne (zwęglone)',
      'Gęstszy niż naturalne drewno - wolniejsze cięcie',
    ],
  },
  Plywood: {
    // Sklejka
    density: 0.68,
    laserAbsorption: 0.92,
    thermalConductivity: 0.13,
    ignitionPoint: 260,
    carbonization: 310,
    toxic: true, // kleje
    cutting: {
      3: { speed: 28, power: 65, frequency: 1000 },
      4: { speed: 20, power: 75, frequency: 1000 },
      5: { speed: 15, power: 80, frequency: 900 },
      6: { speed: 11, power: 85, frequency: 800 },
      8: { speed: 7, power: 90, frequency: 600 },
      10: { speed: 4.5, power: 95, frequency: 500 },
      12: { speed: 3, power: 100, frequency: 400 },
    },
    engraving: {
      speed: 380,
      power: 28,
      frequency: 1000,
      resolution: 0.1,
    },
    notes: [
      'Kleje w sklejce mogą spowalniać cięcie',
      'Możliwe nierówne cięcie między warstwami',
      'Sklejka brzozowa tnie się najlepiej',
      'Unikać sklejki z klejem fenolowym',
    ],
  },
  Paper: {
    density: 0.80,
    laserAbsorption: 0.85,
    thermalConductivity: 0.05,
    ignitionPoint: 230,
    carbonization: 250,
    toxic: false,
    cutting: {
      0.1: { speed: 200, power: 5, frequency: 5000 },
      0.2: { speed: 150, power: 8, frequency: 5000 },
      0.3: { speed: 120, power: 10, frequency: 5000 },
      0.5: { speed: 80, power: 15, frequency: 5000 },
    },
    engraving: {
      speed: 500,
      power: 5,
      frequency: 10000,
      resolution: 0.05,
    },
    notes: [
      'Bardzo niskie moce - łatwo spalić',
      'Możliwe przypalenie krawędzi',
      'Idealny do precyzyjnych wzorów',
    ],
  },
  Fabric: {
    // Tkaniny syntetyczne
    density: 0.30,
    laserAbsorption: 0.80,
    thermalConductivity: 0.04,
    meltingPoint: 150,
    vaporization: 250,
    toxic: false,
    cutting: {
      0.5: { speed: 100, power: 10, frequency: 5000 },
      1: { speed: 70, power: 15, frequency: 5000 },
      2: { speed: 50, power: 20, frequency: 5000 },
      3: { speed: 35, power: 30, frequency: 5000 },
    },
    engraving: {
      speed: 450,
      power: 8,
      frequency: 10000,
      resolution: 0.08,
    },
    notes: [
      'Tkaniny syntetyczne topią krawędzie (zapobiega strzępieniu)',
      'Naturalne tkaniny mogą się palić',
      'Testuj na małym kawałku',
    ],
  },
  Leather: {
    // Skóra naturalna
    density: 0.86,
    laserAbsorption: 0.88,
    thermalConductivity: 0.14,
    ignitionPoint: 210,
    carbonization: 280,
    toxic: false,
    cutting: {
      1: { speed: 40, power: 35, frequency: 1000 },
      2: { speed: 25, power: 50, frequency: 1000 },
      3: { speed: 15, power: 65, frequency: 1000 },
      4: { speed: 10, power: 75, frequency: 800 },
      5: { speed: 7, power: 85, frequency: 600 },
    },
    engraving: {
      speed: 350,
      power: 20,
      frequency: 1000,
      resolution: 0.1,
    },
    notes: [
      'Skóra naturalna - piękne grawerowanie',
      'Skóra chromowana może uwalniać toksyny!',
      'Testuj parametry na odpadach',
    ],
  },
  Rubber: {
    // Guma
    density: 1.52,
    laserAbsorption: 0.91,
    thermalConductivity: 0.13,
    ignitionPoint: 180,
    carbonization: 350,
    toxic: true, // może uwalniać toksyczne gazy
    cutting: {
      1: { speed: 30, power: 50, frequency: 1000 },
      2: { speed: 18, power: 65, frequency: 1000 },
      3: { speed: 12, power: 75, frequency: 800 },
      4: { speed: 8, power: 85, frequency: 600 },
      5: { speed: 5, power: 90, frequency: 500 },
      6: { speed: 3, power: 95, frequency: 400 },
    },
    engraving: {
      speed: 200,
      power: 35,
      frequency: 1000,
      resolution: 0.15,
    },
    notes: [
      '⚠️ Może uwalniać toksyczne gazy - wentylacja!',
      'Dużo dymu podczas cięcia',
      'Krawędzie mogą być lepkie',
      'Idealna do stempli',
    ],
  },
};

// Współczynniki jakości
const qualityFactors = {
  draft: { speedMultiplier: 1.5, powerMultiplier: 0.9, passes: 1 },
  standard: { speedMultiplier: 1.0, powerMultiplier: 1.0, passes: 1 },
  high: { speedMultiplier: 0.7, powerMultiplier: 1.1, passes: 1 },
};

// Dane soczewek
const lensData = {
  1.5: { // 1.5"
    focalLength: 38.1, // mm
    depthOfFocus: 1.5, // mm
    spotSize: 0.08, // mm
    maxThickness: 3, // mm
    kerf: 0.1, // mm
  },
  2: { // 2"
    focalLength: 50.8,
    depthOfFocus: 2.5,
    spotSize: 0.12,
    maxThickness: 6,
    kerf: 0.15,
  },
  2.5: { // 2.5"
    focalLength: 63.5,
    depthOfFocus: 3.5,
    spotSize: 0.15,
    maxThickness: 10,
    kerf: 0.18,
  },
  3: { // 3"
    focalLength: 76.2,
    depthOfFocus: 4.5,
    spotSize: 0.18,
    maxThickness: 15,
    kerf: 0.22,
  },
  4: { // 4"
    focalLength: 101.6,
    depthOfFocus: 6,
    spotSize: 0.25,
    maxThickness: 20,
    kerf: 0.35,
  },
  5: { // 5"
    focalLength: 127,
    depthOfFocus: 8,
    spotSize: 0.35,
    maxThickness: 30,
    kerf: 0.45,
  },
};

export function calculateLaserParameters(params: LaserParameters): LaserResult {
  const material = cuttingDatabase[params.material];
  if (!material) {
    throw new Error(`Nieobsługiwany materiał: ${params.material}`);
  }

  const lens = lensData[params.focalLength];
  if (!lens) {
    throw new Error(`Nieobsługiwana soczewka: ${params.focalLength}"`);
  }

  let result: LaserResult = {
    speed: 0,
    power: 0,
    frequency: 5000,
    passes: 1,
    airAssist: 'medium',
    focalPosition: 0,
    kerf: lens.kerf,
    minCornerSpeed: 0,
    estimatedTime: 0,
    energyUsage: 0,
    warnings: [],
    tips: [],
    safetyNotes: [],
    qualityNotes: [],
  };

  // Sprawdź toksyczność
  if (material.toxic) {
    result.warnings.push(material.warning || '⚠️ UWAGA: Materiał może uwalniać toksyczne opary!');
    result.safetyNotes.push('Wymagana doskonała wentylacja i filtracja');
    result.safetyNotes.push('Używać maski z filtrem');
  }

  // Sprawdź grubość vs soczewka
  if (params.thickness > lens.maxThickness) {
    result.warnings.push(`Soczewka ${params.focalLength}" może nie być optymalna dla grubości ${params.thickness}mm`);
    result.tips.push(`Rozważ użycie soczewki o dłuższej ogniskowej`);
  }

  if (params.operation === 'cut') {
    // Znajdź najbliższą grubość w bazie
    const thicknesses = Object.keys(material.cutting)
      .map(Number)
      .sort((a, b) => a - b);
    
    let baseParams;
    let selectedThickness = params.thickness;
    
    // Interpolacja dla grubości pomiędzy
    if (material.cutting[params.thickness]) {
      baseParams = material.cutting[params.thickness];
    } else {
      // Znajdź sąsiednie grubości
      let lower = 0, upper = 100;
      for (const t of thicknesses) {
        if (t <= params.thickness && t > lower) lower = t;
        if (t >= params.thickness && t < upper) upper = t;
      }
      
      if (material.cutting[lower] && material.cutting[upper]) {
        // Interpolacja liniowa
        const ratio = (params.thickness - lower) / (upper - lower);
        const lowerParams = material.cutting[lower];
        const upperParams = material.cutting[upper];
        
        baseParams = {
          speed: lowerParams.speed - (lowerParams.speed - upperParams.speed) * ratio,
          power: lowerParams.power + (upperParams.power - lowerParams.power) * ratio,
          frequency: Math.round(lowerParams.frequency - (lowerParams.frequency - upperParams.frequency) * ratio),
          passes: upperParams.passes || 1,
        };
      } else if (material.cutting[lower]) {
        // Ekstrapolacja
        baseParams = material.cutting[lower];
        const factor = params.thickness / lower;
        baseParams = {
          speed: baseParams.speed / factor,
          power: Math.min(100, baseParams.power * Math.sqrt(factor)),
          frequency: baseParams.frequency,
          passes: factor > 1.5 ? 2 : 1,
        };
      } else {
        throw new Error(`Brak danych dla grubości ${params.thickness}mm`);
      }
    }

    // Skalowanie dla mocy lasera (bazowe dane są dla 100W)
    const powerScale = params.laserPower / 100;
    result.speed = baseParams.speed * powerScale;
    result.power = Math.min(100, baseParams.power / powerScale);
    result.frequency = baseParams.frequency;
    result.passes = baseParams.passes || 1;

    // Dostosowanie dla jakości
    const qualityFactor = qualityFactors[params.quality];
    result.speed *= qualityFactor.speedMultiplier;
    result.power *= qualityFactor.powerMultiplier;
    
    // Dodatkowe przejście dla wysokiej jakości i grubych materiałów
    if (params.quality === 'high' && params.thickness > 10) {
      result.passes = 2;
    }

    // Pozycja ogniska
    if (params.thickness <= 3) {
      result.focalPosition = 0; // na powierzchni
    } else if (params.thickness <= 10) {
      result.focalPosition = -params.thickness / 2; // w środku materiału
    } else {
      result.focalPosition = -params.thickness / 3; // 1/3 głębokości
      result.tips.push('Dla grubych materiałów rozważ cięcie z dwóch stron');
    }

    // Wydmuch powietrza
    if (params.material === 'PMMA' || params.material === 'PC') {
      result.airAssist = 'low'; // dla czystej krawędzi
      result.tips.push('Niski wydmuch dla polerowanej krawędzi');
    } else if (params.material === 'Wood' || params.material === 'MDF' || params.material === 'Leather') {
      result.airAssist = 'high'; // zapobiega zapłonowi
      result.tips.push('Wysoki wydmuch zapobiega zapłonowi');
    } else {
      result.airAssist = 'medium';
    }

    // Prędkość w narożnikach
    result.minCornerSpeed = Math.max(1, result.speed * 0.3);

  } else if (params.operation === 'engrave') {
    const engraveParams = material.engraving;
    
    result.speed = engraveParams.speed * (params.laserPower / 100);
    result.power = Math.min(100, engraveParams.power / (params.laserPower / 100));
    result.frequency = engraveParams.frequency;
    result.passes = 1;
    result.airAssist = 'low';
    result.focalPosition = 0;
    result.minCornerSpeed = result.speed * 0.5;
    
    result.tips.push(`Rozdzielczość: ${engraveParams.resolution}mm dla najlepszych efektów`);
    
  } else if (params.operation === 'mark') {
    // Znakowanie - bardzo lekkie grawerowanie
    const engraveParams = material.engraving;
    
    result.speed = engraveParams.speed * 1.5;
    result.power = engraveParams.power * 0.5;
    result.frequency = engraveParams.frequency;
    result.passes = 1;
    result.airAssist = 'low';
    result.focalPosition = 0;
    result.minCornerSpeed = result.speed * 0.7;
    
    result.tips.push('Znakowanie - tylko powierzchniowa zmiana');
  }

  // Obliczenia czasowe i energetyczne
  result.estimatedTime = (1000 / result.speed) * result.passes; // s/m
  result.energyUsage = (params.laserPower * result.power / 100) * result.estimatedTime / 3600; // kWh/m

  // Dodaj uwagi specyficzne dla materiału
  if (material.notes) {
    result.tips.push(...material.notes);
  }

  // Uwagi o jakości
  switch (params.quality) {
    case 'draft':
      result.qualityNotes.push('Szybkie cięcie - możliwe niedocięcia');
      result.qualityNotes.push('Krawędź może wymagać czyszczenia');
      break;
    case 'standard':
      result.qualityNotes.push('Optymalna równowaga prędkości i jakości');
      break;
    case 'high':
      result.qualityNotes.push('Wolne cięcie dla najlepszej jakości krawędzi');
      result.qualityNotes.push('Minimalne przypalenia i czystsza krawędź');
      break;
  }

  // Ogólne uwagi BHP
  result.safetyNotes.push('Zawsze używać okularów ochronnych dla CO2 (10.6μm)');
  result.safetyNotes.push('Zapewnić odpowiednią wentylację');
  result.safetyNotes.push('Mieć gaśnicę w zasięgu ręki');
  result.safetyNotes.push('Nigdy nie pozostawiać lasera bez nadzoru');

  // Zaokrąglij wartości
  result.speed = Math.round(result.speed * 10) / 10;
  result.power = Math.round(result.power);
  result.minCornerSpeed = Math.round(result.minCornerSpeed * 10) / 10;
  result.estimatedTime = Math.round(result.estimatedTime * 10) / 10;
  result.energyUsage = Math.round(result.energyUsage * 1000) / 1000;

  return result;
}

// Funkcja pomocnicza - generowanie G-code
export function generateGCode(params: LaserParameters, result: LaserResult, shape: 'rectangle' | 'circle' | 'custom', dimensions: any): string {
  let gcode = [];
  
  // Nagłówek
  gcode.push('; PlexiSystem Laser CO2 G-Code');
  gcode.push(`; Material: ${params.material}`);
  gcode.push(`; Thickness: ${params.thickness}mm`);
  gcode.push(`; Operation: ${params.operation}`);
  gcode.push(`; Date: ${new Date().toISOString()}`);
  gcode.push('');
  
  // Inicjalizacja
  gcode.push('G90 ; Absolute positioning');
  gcode.push('G21 ; Units in mm');
  gcode.push('M5 ; Laser off');
  gcode.push('G0 F6000 ; Travel speed');
  gcode.push('G0 X0 Y0 ; Home');
  gcode.push('');
  
  // Ustawienia lasera
  gcode.push(`M649 S${result.power} ; Set laser power ${result.power}%`);
  gcode.push(`M650 F${result.frequency} ; Set frequency ${result.frequency}Hz`);
  gcode.push('');
  
  // Generowanie ścieżki
  if (shape === 'rectangle') {
    const { width, height } = dimensions;
    gcode.push(`G0 X0 Y0 ; Start position`);
    gcode.push(`G1 F${result.speed * 60} ; Cutting speed mm/min`);
    gcode.push('M3 ; Laser on');
    gcode.push(`G1 X${width} Y0`);
    gcode.push(`G1 X${width} Y${height}`);
    gcode.push(`G1 X0 Y${height}`);
    gcode.push(`G1 X0 Y0`);
    gcode.push('M5 ; Laser off');
  } else if (shape === 'circle') {
    const { diameter } = dimensions;
    const radius = diameter / 2;
    gcode.push(`G0 X${radius} Y0 ; Start position`);
    gcode.push(`G1 F${result.speed * 60} ; Cutting speed`);
    gcode.push('M3 ; Laser on');
    gcode.push(`G2 X${radius} Y0 I-${radius} J0 ; Full circle`);
    gcode.push('M5 ; Laser off');
  }
  
  // Jeśli potrzebne dodatkowe przejścia
  if (result.passes > 1) {
    gcode.push('');
    gcode.push(`; Pass 2 of ${result.passes}`);
    // Powtórz ścieżkę
  }
  
  // Zakończenie
  gcode.push('');
  gcode.push('M5 ; Laser off');
  gcode.push('G0 X0 Y0 ; Return home');
  gcode.push('M2 ; Program end');
  
  return gcode.join('\n');
}

// Funkcja do estymacji czasu dla złożonych kształtów
export function estimateCuttingTime(
  pathLength: number, // mm
  params: LaserParameters,
  result: LaserResult
): { time: number; cost: number } {
  const cuttingTime = (pathLength / 1000) * result.estimatedTime * result.passes; // sekundy
  const energyCost = result.energyUsage * (pathLength / 1000) * 0.65; // PLN (0.65 PLN/kWh)
  const laborCost = (cuttingTime / 3600) * 120; // PLN (120 PLN/h)
  
  return {
    time: Math.round(cuttingTime),
    cost: Math.round((energyCost + laborCost) * 100) / 100,
  };
}

// Walidacja parametrów
export function validateLaserSetup(params: LaserParameters): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Sprawdź moc lasera
  if (params.laserPower < 30) {
    errors.push('Moc lasera < 30W może być niewystarczająca dla większości materiałów');
  }
  
  if (params.laserPower > 200) {
    errors.push('Moc lasera > 200W wymaga specjalnych środków bezpieczeństwa');
  }
  
  // Sprawdź grubość
  if (params.thickness > 30) {
    errors.push('Grubości > 30mm są bardzo trudne do cięcia laserem CO2');
  }
  
  // Sprawdź materiał
  if (params.material === 'PC' && params.thickness > 6) {
    errors.push('Poliwęglan > 6mm - rozważ cięcie mechaniczne ze względu na toksyczne opary');
  }
  
  // Sprawdź soczewkę
  const lens = lensData[params.focalLength];
  if (lens && params.thickness > lens.maxThickness * 1.5) {
    errors.push(`Soczewka ${params.focalLength}" nie jest odpowiednia dla tej grubości`);
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}